// The metric catalogue (spec/21 §6).
//
// Every metric the platform may emit, declared once. The server builds its instruments from this
// list, the dashboards are generated from it, and the alert rules are checked against it — so a
// panel cannot name a metric nothing emits and a metric cannot be emitted under a name no panel
// knows.
//
// ---------------------------------------------------------------------------------------------
// `source` is the honest part
// ---------------------------------------------------------------------------------------------
//
// Most of what spec/21 §6 asks a dashboard to show does not exist yet: there are no payments, no
// ledger, no OTP. A dashboard for those could be built today and it would
// render, and every panel on it would read zero for ever, and it would look exactly like a
// dashboard that works. This project has shipped that failure — a green signal measuring nothing —
// eight times in one day.
//
// So each metric says which it is:
//
//   { kind: 'emitted' }   something in this repository writes this metric today, and `by` says
//                         which file. `createMetrics()` will build an instrument for it.
//   { kind: 'pending' }   nothing can emit it until `needs` lands. `createMetrics()` **refuses**
//                         to build an instrument for it, so there is no way to publish a zero and
//                         call it data, and the generated dashboard marks the panel "no data
//                         source yet" with the issue on its face.
//
// Moving a metric from `pending` to `emitted` is a one-line diff in the pull request that makes it
// true, next to the code that emits it.
import { z } from "../zod.js";
export const METRIC_KINDS = ['counter', 'updownCounter', 'gauge', 'histogram'];
// ---------------------------------------------------------------------------------------------
// The catalogue
// ---------------------------------------------------------------------------------------------
const CATALOGUE = [
    // -------------------------------------------------------------------------------------------
    // HTTP — the availability and latency SLOs, and per-endpoint latency and error rate
    // -------------------------------------------------------------------------------------------
    {
        name: 'http.server.request.duration',
        kind: 'histogram',
        unit: 's',
        description: 'How long the server took to answer one request. The p50/p95/p99 latency SLO and, with ' +
            'http.response.status_code, the availability and error-rate SLOs (spec/21 §1).',
        attributes: ['http.request.method', 'http.route', 'http.response.status_code', 'clavian.entrypoint'],
        source: { kind: 'emitted', by: 'apps/server/src/http/telemetry.ts' },
    },
    {
        name: 'http.server.active_requests',
        kind: 'updownCounter',
        unit: '{request}',
        description: 'Requests in flight. Saturation, and the number an autoscaler reads (spec/21 §9).',
        attributes: ['http.request.method', 'clavian.entrypoint'],
        source: { kind: 'emitted', by: 'apps/server/src/http/telemetry.ts' },
    },
    {
        name: 'clavian.readiness.failures',
        kind: 'counter',
        unit: '{check}',
        description: 'A dependency check that said "do not send me traffic". This is what a failing /readyz ' +
            'reports instead of a log line: the probe itself is never logged (spec/19 §7 rule 5).',
        attributes: ['clavian.check', 'clavian.entrypoint'],
        source: { kind: 'emitted', by: 'apps/server/src/http/health.ts' },
    },
    // -------------------------------------------------------------------------------------------
    // Errors (Sentry-class error tracking, spec/21 §6)
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.errors',
        kind: 'counter',
        unit: '{error}',
        description: 'One unhandled or recorded error, tagged with the release it happened in. The count behind ' +
            'the elevated-errors ticket; the error itself is on the span that recorded it.',
        attributes: ['clavian.surface', 'clavian.error.kind', 'error.type', 'service.version'],
        source: { kind: 'emitted', by: 'apps/server/src/observability/errors.ts' },
    },
    // -------------------------------------------------------------------------------------------
    // The cost of telemetry itself — the rules in spec/19 §7, with a number on them
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.telemetry.log_lines',
        kind: 'counter',
        unit: '{line}',
        description: 'Log lines actually written. The input to the log-volume cost anomaly ticket.',
        attributes: ['clavian.log.level', 'clavian.entrypoint'],
        source: { kind: 'emitted', by: 'apps/server/src/observability/logger.ts' },
    },
    {
        name: 'clavian.telemetry.log_lines_suppressed',
        kind: 'counter',
        unit: '{line}',
        description: 'Log lines the cost rule refused, by operation. This is how "heartbeats are never logged" ' +
            'is visible as a number rather than as an absence — a rule whose only evidence is that ' +
            'nothing happened is a rule nobody can tell has stopped working (spec/19 §7 rule 1).',
        attributes: ['clavian.operation'],
        source: { kind: 'emitted', by: 'apps/server/src/observability/logger.ts' },
    },
    {
        name: 'clavian.telemetry.sampling_decisions',
        kind: 'counter',
        unit: '{span}',
        description: 'Root spans, by what the sampler decided: recorded, dropped by the ratio, or dropped ' +
            'because the operation is silent. Proves the sampler is dropping rather than configured to.',
        attributes: ['clavian.sampling.decision'],
        source: { kind: 'emitted', by: 'apps/server/src/observability/sampling.ts' },
    },
    // -------------------------------------------------------------------------------------------
    // Queue depth and job lag — issue #33's one unmet acceptance criterion
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.outbox.backlog',
        kind: 'gauge',
        unit: '{event}',
        description: 'Outbox rows written and not yet dispatched. Queue depth for domain events (spec/03 §8).',
        attributes: [],
        source: { kind: 'emitted', by: 'apps/server/src/modules/integrations/jobs.ts' },
    },
    {
        name: 'clavian.outbox.lag',
        kind: 'gauge',
        unit: 'ms',
        description: 'How old the oldest undispatched outbox row is. Job lag: a backlog of ten that is four ' +
            'hours old is a stuck dispatcher, and a backlog of ten thousand that is two seconds old is ' +
            'a busy evening.',
        attributes: [],
        source: { kind: 'emitted', by: 'apps/server/src/modules/integrations/jobs.ts' },
    },
    {
        name: 'clavian.outbox.dead_lettered',
        kind: 'gauge',
        unit: '{delivery}',
        description: 'Deliveries that ran out of attempts. Above zero means an event that should have had an ' +
            'effect never had one, and no further retry will change that.',
        attributes: [],
        source: { kind: 'emitted', by: 'apps/server/src/modules/integrations/jobs.ts' },
    },
    {
        name: 'clavian.outbox.retrying',
        kind: 'gauge',
        unit: '{delivery}',
        description: 'Deliveries waiting on their backoff. Healthy in ones; a provider outage in hundreds.',
        attributes: [],
        source: { kind: 'emitted', by: 'apps/server/src/modules/integrations/jobs.ts' },
    },
    {
        name: 'clavian.outbox.deliveries',
        kind: 'counter',
        unit: '{delivery}',
        description: 'One attempt to deliver one event to one sink, by sink and outcome (spec/03 §8).',
        attributes: ['clavian.delivery.sink', 'clavian.delivery.outcome'],
        source: { kind: 'emitted', by: 'apps/server/src/modules/integrations/dispatcher.ts' },
    },
    {
        name: 'clavian.queue.depth',
        kind: 'gauge',
        unit: '{job}',
        description: 'pg-boss jobs by queue and state — ready, active, failed (ADR-0006).',
        attributes: ['clavian.queue', 'clavian.queue.state'],
        source: { kind: 'emitted', by: 'apps/server/src/jobs/scheduler.ts' },
    },
    {
        name: 'clavian.job.duration',
        kind: 'histogram',
        unit: 's',
        description: 'How long one scheduled job took. A job that starts taking minutes is the next backlog.',
        attributes: ['clavian.job', 'clavian.job.outcome'],
        source: { kind: 'emitted', by: 'apps/server/src/jobs/scheduler.ts' },
    },
    {
        name: 'clavian.job.runs',
        kind: 'counter',
        unit: '{run}',
        description: 'Scheduled job runs by outcome. A job that has stopped running has a flat line here.',
        attributes: ['clavian.job', 'clavian.job.outcome'],
        source: { kind: 'emitted', by: 'apps/server/src/jobs/scheduler.ts' },
    },
    // -------------------------------------------------------------------------------------------
    // Backing services
    // -------------------------------------------------------------------------------------------
    {
        name: 'db.client.operation.duration',
        kind: 'histogram',
        unit: 's',
        description: 'One statement against Postgres, from the executor the server already wraps every query in.',
        attributes: ['db.system.name', 'db.operation.name', 'clavian.entrypoint'],
        source: { kind: 'emitted', by: 'apps/server/src/db/client.ts' },
    },
    {
        name: 'clavian.valkey.command.duration',
        kind: 'histogram',
        unit: 's',
        description: 'One Valkey command. Presence, the caches and the rate-limit counters all run through here, ' +
            'so a Valkey that has gone slow shows up before it shows up as request latency.',
        attributes: ['clavian.valkey.command', 'clavian.entrypoint'],
        source: { kind: 'emitted', by: 'apps/server/src/valkey/client.ts' },
    },
    // -------------------------------------------------------------------------------------------
    // Station fleet health
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.station.heartbeats',
        kind: 'counter',
        unit: '{heartbeat}',
        description: 'Station heartbeats, counted. Never logged and never written to Postgres: a heartbeat is a ' +
            'TTL refresh in Valkey and one increment here (spec/19 §7 rule 1).',
        attributes: ['clavian.heartbeat.outcome'],
        source: { kind: 'emitted', by: 'apps/server/src/observability/heartbeat.ts' },
    },
    {
        name: 'clavian.station.presence_changes',
        kind: 'counter',
        unit: '{change}',
        description: 'A station that changed state — connected, went offline, changed mode. Only these reach ' +
            'Postgres; the heartbeats between them do not (spec/09 §8).',
        attributes: ['clavian.station.change'],
        source: { kind: 'emitted', by: 'apps/server/src/observability/heartbeat.ts' },
    },
    {
        name: 'clavian.station.online',
        kind: 'gauge',
        unit: '{station}',
        description: 'Stations with a live socket, per venue. The fleet-health headline and the mass-offline page.',
        attributes: ['clavian.venue'],
        source: { kind: 'emitted', by: 'apps/server/src/realtime/stations.ts' },
    },
    {
        name: 'clavian.station.health',
        kind: 'gauge',
        unit: '1',
        description: 'Station self-reported health: disk, memory, the lock hook, the policy version (spec/10 §9).',
        attributes: ['clavian.venue', 'clavian.station.health.check'],
        source: { kind: 'pending', needs: 'station health reporting', owner: 'F-STN-11 (#89)' },
    },
    // -------------------------------------------------------------------------------------------
    // Realtime
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.realtime.connections',
        kind: 'updownCounter',
        unit: '{connection}',
        description: 'Open sockets per gateway node. Sized against the socket ceiling in spec/19 §2.',
        attributes: ['clavian.node', 'clavian.principal.kind'],
        source: { kind: 'emitted', by: 'apps/server/src/realtime/gateway.ts' },
    },
    {
        name: 'clavian.realtime.connects',
        kind: 'counter',
        unit: '{connection}',
        description: 'Sockets that authenticated, by principal. The rate is the reconnect-storm signal: a fleet coming ' +
            'back at once shows here before it shows as latency (spec/19 §7 rule 9).',
        attributes: ['clavian.principal.kind'],
        source: { kind: 'emitted', by: 'apps/server/src/realtime/gateway.ts' },
    },
    {
        name: 'clavian.realtime.closes',
        kind: 'counter',
        unit: '{connection}',
        description: 'Sockets the gateway closed, by close code (spec/09 §9). 4401 is a token problem; 1013 a slow consumer.',
        attributes: ['clavian.close.code'],
        source: { kind: 'emitted', by: 'apps/server/src/realtime/gateway.ts' },
    },
    {
        name: 'clavian.realtime.slow_consumers',
        kind: 'counter',
        unit: '{event}',
        description: 'A socket whose send buffer passed 1 MiB and was switched to merged updates, or passed 4 MiB and ' +
            'was closed (spec/09 §8). The slow-consumer signal.',
        attributes: ['clavian.backpressure.action'],
        source: { kind: 'emitted', by: 'apps/server/src/realtime/gateway.ts' },
    },
    {
        name: 'clavian.realtime.delivery.duration',
        kind: 'histogram',
        unit: 's',
        description: 'Event to client. The ≤ 500 ms p95 realtime delivery SLO (spec/21 §1).',
        attributes: ['clavian.channel.kind'],
        source: { kind: 'emitted', by: 'apps/server/src/realtime/gateway.ts' },
    },
    {
        name: 'clavian.realtime.command.ack.duration',
        kind: 'histogram',
        unit: 's',
        description: 'Staff action to the station acknowledging it. The ≤ 1 s p95 command-ack SLO (spec/21 §1).',
        attributes: ['clavian.command'],
        source: { kind: 'emitted', by: 'apps/server/src/realtime/stations.ts' },
    },
    // -------------------------------------------------------------------------------------------
    // Sessions and the kiosk
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.session.sign_in.duration',
        kind: 'histogram',
        unit: 's',
        description: 'Sign-in to a playable desktop. The ≤ 3 s p95 kiosk SLO (spec/21 §1).',
        attributes: ['clavian.venue', 'clavian.sign_in.method'],
        source: { kind: 'pending', needs: 'sign-in on the PC', owner: 'F-SES-01 (#255)' },
    },
    {
        name: 'clavian.session.started',
        kind: 'counter',
        unit: '{session}',
        description: 'Sessions started, per venue. Half the session-anomaly business monitor (spec/21 §6).',
        attributes: ['clavian.venue'],
        source: { kind: 'pending', needs: 'sessions', owner: 'F-SES-01 (#255)' },
    },
    // -------------------------------------------------------------------------------------------
    // Payments, settlement and the ledger
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.payment.attempts',
        kind: 'counter',
        unit: '{payment}',
        description: 'Payment attempts by provider, method and outcome. The payment-success panel.',
        attributes: ['clavian.payment.provider', 'clavian.payment.method', 'clavian.payment.outcome'],
        source: { kind: 'pending', needs: 'online payments', owner: 'F-PAY-03 (#149)' },
    },
    {
        name: 'clavian.payment.webhook.latency',
        kind: 'histogram',
        unit: 's',
        description: 'Provider webhook to the order being marked paid. The ≤ 5 s p95 SLO (spec/21 §1).',
        attributes: ['clavian.payment.provider'],
        source: { kind: 'pending', needs: 'online payments', owner: 'F-PAY-03 (#149)' },
    },
    {
        name: 'clavian.payment.settlement.lag',
        kind: 'histogram',
        unit: 's',
        description: 'Capture to settled funds. The settlement-lag panel and the settlement-failure page.',
        attributes: ['clavian.payment.provider'],
        source: { kind: 'pending', needs: 'settlement reconciliation', owner: 'F-PAY-08 (#154)' },
    },
    {
        name: 'clavian.refunds',
        kind: 'counter',
        unit: '{refund}',
        description: 'Refunds issued. The refund-spike business monitor (spec/21 §6).',
        attributes: ['clavian.venue', 'clavian.refund.reason'],
        source: { kind: 'pending', needs: 'online refunds', owner: 'F-PAY-07 (#153)' },
    },
    {
        name: 'clavian.revenue',
        kind: 'counter',
        unit: '{minor unit}',
        description: 'Revenue recognised, as an integer in the currency minor unit with the ISO 4217 code as an ' +
            'attribute — never a float and never rupees (CLAUDE.md, spec/06 §1). The revenue-anomaly monitor.',
        attributes: ['clavian.venue', 'clavian.currency'],
        source: { kind: 'pending', needs: 'the double-entry ledger', owner: 'F-PAY-10 (#147)' },
    },
    {
        name: 'clavian.ledger.integrity.runs',
        kind: 'counter',
        unit: '{run}',
        description: 'The ledger integrity job, by outcome. A run that is absent is as bad as one that failed.',
        attributes: ['clavian.job.outcome'],
        source: { kind: 'pending', needs: 'the double-entry ledger', owner: 'F-PAY-10 (#147)' },
    },
    {
        name: 'clavian.ledger.imbalance',
        kind: 'gauge',
        unit: '{minor unit}',
        description: 'Debits minus credits at the last integrity run, in the currency minor unit. Anything but ' +
            'zero pages somebody (spec/06 §2.4).',
        attributes: ['clavian.currency'],
        source: { kind: 'pending', needs: 'the double-entry ledger', owner: 'F-PAY-10 (#147)' },
    },
    // -------------------------------------------------------------------------------------------
    // OTP and notifications — the biggest variable cost in the product (spec/19 §2, §7 rules 3–4)
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.otp.sent',
        kind: 'counter',
        unit: '{message}',
        description: 'One-time code sends, one per attempt, by channel (sms, whatsapp, email) and outcome (sent, ' +
            'failed, timeout, unavailable). WhatsApp first, DLT-registered SMS as the fallback: SMS is ' +
            'about sixty times the price, so the split between the two is the cost panel (spec/19 §2). ' +
            '`unavailable` is a code somebody asked for that no configured provider could carry.',
        attributes: ['clavian.message.channel', 'clavian.otp.outcome'],
        // Phone codes are counted by the router in `otp.ts`; email codes by `email-code.ts` (#407).
        source: { kind: 'emitted', by: 'apps/server/src/modules/notifications/email-code.ts' },
    },
    {
        name: 'clavian.otp.cost',
        kind: 'counter',
        unit: '{minor unit}',
        description: 'What the OTPs cost, in the billing currency minor unit with the ISO 4217 code as an attribute.',
        attributes: ['clavian.message.channel', 'clavian.currency'],
        source: { kind: 'pending', needs: 'phone + OTP sign-in', owner: 'F-IAM-01 (#42)' },
    },
    {
        name: 'clavian.notification.sent',
        kind: 'counter',
        unit: '{message}',
        description: 'Notifications delivered, by channel and category (spec/16 §6).',
        attributes: ['clavian.message.channel', 'clavian.notification.category', 'clavian.notification.outcome'],
        source: { kind: 'pending', needs: 'the notification delivery pipeline', owner: 'F-NTF-03 (#191)' },
    },
    {
        name: 'clavian.notification.cost',
        kind: 'counter',
        unit: '{minor unit}',
        description: 'What the notifications cost, in the billing currency minor unit.',
        attributes: ['clavian.message.channel', 'clavian.currency'],
        source: { kind: 'pending', needs: 'the notification delivery pipeline', owner: 'F-NTF-03 (#191)' },
    },
    // -------------------------------------------------------------------------------------------
    // Venue operations — the business monitors in spec/21 §6
    // -------------------------------------------------------------------------------------------
    {
        name: 'clavian.venue.open',
        kind: 'gauge',
        unit: '1',
        description: 'Whether a venue is inside its opening hours, in the venue timezone. On its own it is ' +
            'nothing; against clavian.station.online it is "a venue is dark while it should be trading".',
        attributes: ['clavian.venue'],
        source: { kind: 'pending', needs: 'venue profile and opening hours', owner: 'F-ORG-01 (#64)' },
    },
    {
        name: 'clavian.cash.variance',
        kind: 'gauge',
        unit: '{minor unit}',
        description: 'Counted cash minus expected at shift close, in the currency minor unit. The variance monitor.',
        attributes: ['clavian.venue', 'clavian.currency'],
        source: { kind: 'pending', needs: 'close shift with a blind count', owner: 'F-OPS-03 (#168)' },
    },
    {
        name: 'clavian.auth.sign_ins',
        kind: 'counter',
        unit: '{sign_in}',
        description: 'Sign-in attempts by method and outcome. A collapse in the success rate is the auth-outage page.',
        attributes: ['clavian.sign_in.method', 'clavian.sign_in.outcome'],
        source: { kind: 'pending', needs: 'better-auth base configuration', owner: 'INFRA-09 (#34)' },
    },
];
/**
 * The catalogue, widened to the interface so that iterating it is ordinary code. The literal union
 * beside it is what turns a mistyped metric name into a compile error rather than an empty graph.
 */
export const METRICS = CATALOGUE;
const BY_NAME = new Map(METRICS.map((metric) => [metric.name, metric]));
export class MetricCatalogueError extends Error {
    name = 'MetricCatalogueError';
}
export function findMetric(name) {
    return BY_NAME.get(name);
}
/**
 * The definition, or a thrown error. Used where a name has been written down by hand — a dashboard
 * panel, an alert rule — so a typo is a failed build rather than an empty graph.
 */
export function requireMetric(name) {
    const metric = BY_NAME.get(name);
    if (metric === undefined) {
        throw new MetricCatalogueError(`"${name}" is not in the metric catalogue (packages/contracts/src/telemetry).`);
    }
    return metric;
}
/** The metrics something in this repository writes today. These are the only ones with instruments. */
export function emittedMetrics() {
    return METRICS.filter((metric) => metric.source.kind === 'emitted');
}
/** The metrics whose feature has not landed. A panel on one of these says so on its face. */
export function pendingMetrics() {
    return METRICS.filter((metric) => metric.source.kind === 'pending');
}
export function isEmitted(name) {
    return findMetric(name)?.source.kind === 'emitted';
}
export const MetricKindSchema = z.enum(METRIC_KINDS);
//# sourceMappingURL=metrics.js.map